import ModalSurface from '../shared/ModalSurface';
import '../../styles/restScreens.css';
import { useEffect, useState } from 'react';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import { buildFlightCapture, inferFlightDirection } from '../../lib/flightPickup';
import { useCompanyWords } from '../../context/CompanyWordsContext';

// Create / edit form for an engagement. Status is NOT edited here — status moves
// happen through the lifecycle actions on the detail view (mirroring the DB ratchet).
// New engagements are always created in 'requested'; the confirmation number is
// stamped later, by the database, at the Requested → Confirmed step.
// Optional pre-fill props (used when the modal is opened from a calendar slot click):
//   initialDate       — 'YYYY-MM-DD' string for the pickup date
//   initialTime       — 'HH:MM' string for the pickup time
//   initialEntityId   — entity UUID to pre-select in the Hub dropdown
function EngagementFormModal({
    mode = 'create',
    engagement = null,
    entities = [],
    accounts = [],
    services = [],
    zones = [],
    serviceLocations = [],
    serviceZoneRates = [],
    catalogError = null,
    orgId = null,
    onSearchClients,
    onClose,
    onSave,
    theme = 'light',
    initialDate = null,
    initialTime = null,
    initialEntityId = null,
    showTripPrices = true,
    showPaymentStatus = showTripPrices,
    canEditTripPrices = true,
    canEditFinalAmounts = canEditTripPrices,
    isQuote = false
}) {
    const words = useCompanyWords();
    const dark = theme === 'dark';
    const isEdit = mode === 'edit';

    // Build the initial datetime-local value from pre-fill props when provided.
    const initialPickupDatetime = (() => {
        if (engagement?.schedule?.scheduled_start) return engagement.schedule.scheduled_start.slice(0, 16);
        if (initialDate && initialTime) return `${initialDate}T${initialTime}`;
        if (initialDate) return `${initialDate}T00:00`;
        return '';
    })();

    const [form, setForm] = useState({
        entity_id: initialEntityId || engagement?.entity_id || (entities[0]?.id || ''),
        account_id: engagement?.account_id || '',
        rider_id: engagement?.rider_id || '',
        client_label: '',
        service_id: engagement?.service_id || '',
        service_type: engagement?.service_type || '',
        zone_id: engagement?.zone_id || '',
        scheduled_start: initialPickupDatetime,
        origin: engagement?.origin || '',
        destination: engagement?.destination || '',
        pickup_address: engagement?.pickup_address || '',
        dropoff_address: engagement?.dropoff_address || '',
        notes: engagement?.notes || '',
        quoted_amount: engagement?.quoted_amount ?? '',
        final_amount: engagement?.final_amount ?? '',
        payment_status: engagement?.payment_status || 'unpaid',
        // FA-2: the flight, captured into the trip's flight fields (not the notes)
        flight_info: engagement?.flight?.flight_number || '',
        flight_direction: engagement?.flight?.flight_direction || '',
        flight_airport: engagement?.flight?.airport_code || '',
        flight_terminal: engagement?.flight?.flight_terminal_type_source === 'captured' ? (engagement?.flight?.flight_terminal_type || '') : '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [clientQuery, setClientQuery] = useState('');
    const [clientOptions, setClientOptions] = useState([]);
    const [clientSearchError, setClientSearchError] = useState(null);
    const [searchingClients, setSearchingClients] = useState(false);

    useEffect(() => {
        const query = clientQuery.trim();
        if (!onSearchClients || query.length < 2) {
            setClientOptions([]);
            setClientSearchError(null);
            setSearchingClients(false);
            return undefined;
        }

        let active = true;
        const timer = setTimeout(async () => {
            setSearchingClients(true);
            setClientSearchError(null);
            try {
                const results = await onSearchClients(query);
                if (active) setClientOptions(results);
            } catch (searchError) {
                if (active) {
                    setClientOptions([]);
                    setClientSearchError(searchError?.message || 'Unable to search clients.');
                }
            } finally {
                if (active) setSearchingClients(false);
            }
        }, 250);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [clientQuery, onSearchClients]);

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const selectedService = services.find((service) => service.id === form.service_id);
    const selectedServiceLocations = serviceLocations.filter((location) => location.service_id === form.service_id);
    const selectedServiceZoneRate = serviceZoneRates.find((rate) =>
        rate.service_id === form.service_id && rate.zone_id === form.zone_id
    );

    const calculatedFare = form.quoted_amount;

    const chooseService = (serviceId) => {
        const service = services.find((candidate) => candidate.id === serviceId);
        const zoneRate = serviceZoneRates.find((rate) => rate.service_id === serviceId && rate.zone_id === form.zone_id);
        const fixedLocations = serviceLocations.filter((location) => location.service_id === serviceId);
        setForm((previous) => ({
            ...previous,
            service_id: serviceId,
            service_type: service?.name || '',
            dropoff_address: fixedLocations.length === 1 ? fixedLocations[0].address : previous.dropoff_address,
            quoted_amount: service
                ? (Number.parseFloat(service.base_price || 0) + Number.parseFloat(zoneRate?.rate || 0)).toFixed(2)
                : '',
        }));
    };

    const chooseZone = (zoneId) => {
        const zoneRate = serviceZoneRates.find((rate) => rate.service_id === form.service_id && rate.zone_id === zoneId);
        setForm((previous) => ({
            ...previous,
            zone_id: zoneId,
            quoted_amount: selectedService
                ? (Number.parseFloat(selectedService.base_price || 0) + Number.parseFloat(zoneRate?.rate || 0)).toFixed(2)
                : previous.quoted_amount,
        }));
    };

    const inputCls = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
        'bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]'
    }`;
    const labelCls = `block text-sm font-medium mb-2 ${dark ? 'text-gray-300' : 'text-gray-700'}`;

    const handleSubmit = async () => {
        setError(null);
        if (!form.entity_id) { setError('Please choose a hub — the confirmation number needs it.'); return; }
        if (!form.service_id || !selectedService) { setError('Choose a service from the catalog.'); return; }

        // Normalize: empty strings → null, amounts → number or null.
        const clean = (v) => (v === '' || v == null ? null : v);
        const num = (v) => (v === '' || v == null ? null : parseFloat(v));

        let flight = null;
        if (String(form.flight_info || '').trim()) {
            flight = buildFlightCapture({
                flightInfo: form.flight_info, pickup: form.pickup_address, dropoff: form.dropoff_address,
                direction: form.flight_direction || null, airportCode: form.flight_airport || null, terminalType: form.flight_terminal || null,
            });
            if (!flight) { setError('The flight number does not look like an airline and number (for example DL30 or Delta 30).'); return; }
        }

        const payload = {
            entity_id: form.entity_id,
            account_id: clean(form.account_id),
            rider_id: clean(form.rider_id),
            service_id: form.service_id,
            service_label: selectedService.name,
            service_type: selectedService.name,
            zone_id: clean(form.zone_id),
            scheduled_start: clean(form.scheduled_start),
            origin: clean(form.origin),
            destination: clean(form.destination),
            pickup_address: clean(form.pickup_address),
            dropoff_address: clean(form.dropoff_address),
            notes: clean(form.notes),
            ...(showTripPrices && canEditTripPrices ? {
                quoted_amount: num(calculatedFare)
            } : {}),
            ...(showTripPrices && canEditFinalAmounts ? { final_amount: num(form.final_amount) } : {}),
            flight
        };

        try {
            setSaving(true);
            await onSave(payload);
        } catch (e) {
            setError(e?.message || 'Something went wrong while saving.');
            setSaving(false);
        }
    };

    return (
        <ModalSurface closeOnEscape={false} onClose={onClose} label={words.booking} overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" panelClassName={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${dark ? 'bg-gray-800' : 'bg-white'}`} panelProps={{"data-m6-layout":"engagements","data-m6-component":"EngagementFormModal"}}>
                {/* Header */}
                <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <h2 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>
                        {isEdit ? `✏️ Edit ${words.booking}` : `➕ New ${words.booking}`}
                    </h2>
                    <button onClick={onClose} className={`ia-modal-desktop-close text-2xl ${dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>×</button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-5">
                    {error && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    {catalogError && (
                        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            Service catalog unavailable: {catalogError}
                        </div>
                    )}

                    {!isEdit && (
                        <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                            New engagements start as <strong>Requested</strong>. The permanent confirmation
                            number is issued automatically the moment you confirm it.
                        </p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Hub */}
                        <div>
                            <label className={labelCls}>Hub <span className="required-asterisk">*</span></label>
                            <select value={form.entity_id} onChange={(e) => set('entity_id', e.target.value)} className={inputCls}>
                                <option value="">Select a hub…</option>
                                {entities.map((ent) => (
                                    <option key={ent.id} value={ent.id}>{ent.entity_code}{ent.name ? ` — ${ent.name}` : ''}</option>
                                ))}
                            </select>
                            <p className={`text-xs mt-1 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Sets the confirmation-number prefix (e.g. ATL).</p>
                        </div>

                        {/* Client / Account */}
                        <div>
                            <label className={labelCls}>Client</label>
                            <input
                                type="search"
                                role="combobox"
                                value={clientQuery || (form.client_label || accounts.find((account) => account.id === form.account_id)?.account_name || '')}
                                onChange={(event) => {
                                    setClientQuery(event.target.value);
                                    setForm((previous) => ({
                                        ...previous,
                                        account_id: '',
                                        rider_id: '',
                                        client_label: '',
                                    }));
                                }}
                                onFocus={() => {
                                    if (!clientQuery && form.client_label) setClientQuery(form.client_label);
                                }}
                                placeholder="Search accounts or riders"
                                autoComplete="off"
                                aria-autocomplete="list"
                                aria-expanded={clientOptions.length > 0}
                                aria-controls="engagement-client-suggestions"
                                className={inputCls}
                            />
                            {clientQuery.trim().length >= 2 && (
                                <div id="engagement-client-suggestions" role="listbox" className="mt-1 max-h-48 overflow-auto rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] shadow-lg">
                                    {searchingClients && <p className="px-3 py-2 text-sm text-[var(--ink-muted)]">Searching…</p>}
                                    {clientSearchError && <p role="alert" className="px-3 py-2 text-sm text-red-600">{clientSearchError}</p>}
                                    {!searchingClients && !clientSearchError && clientOptions.length === 0 && (
                                        <p className="px-3 py-2 text-sm text-[var(--ink-muted)]">No matching accounts or riders.</p>
                                    )}
                                    {clientOptions.map((option) => (
                                        <button
                                            key={`${option.kind}:${option.id}`}
                                            type="button"
                                            role="option"
                                            aria-selected="false"
                                            onClick={() => {
                                                setForm((previous) => ({
                                                    ...previous,
                                                    account_id: option.kind === 'account' ? option.id : option.account_id || '',
                                                    rider_id: option.kind === 'rider' ? option.id : '',
                                                    client_label: option.label,
                                                }));
                                                setClientQuery('');
                                                setClientOptions([]);
                                            }}
                                            className="block w-full px-3 py-2 text-left text-sm text-[var(--ink)] hover:bg-[var(--surface-sunken)]"
                                        >
                                            <span>{option.label}</span>
                                            <span className="ml-2 text-xs text-[var(--ink-muted)]">{option.kind === 'account' ? 'Account' : 'Rider'}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {(form.account_id || form.rider_id) && (
                                <button
                                    type="button"
                                    className="mt-1 text-xs text-[var(--accent-ink)] underline"
                                    onClick={() => {
                                        setForm((previous) => ({ ...previous, account_id: '', rider_id: '', client_label: '' }));
                                        setClientQuery('');
                                    }}
                                >
                                    Clear selection
                                </button>
                            )}
                            <p className={`text-xs mt-1 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Search accounts and riders; results load as you type.</p>
                        </div>

                        {/* Catalog service */}
                        <div>
                            <label className={labelCls}>Service Type <span className="required-asterisk">*</span></label>
                            <select value={form.service_id} onChange={(event) => chooseService(event.target.value)} className={inputCls} disabled={!services.length}>
                                <option value="">{services.length ? 'Select a service…' : 'No active services configured'}</option>
                                {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
                            </select>
                        </div>

                        {/* Pickup Date / Time */}
                        <div>
                            <label className={labelCls}>Pickup Date &amp; Time</label>
                            <input
                                type="datetime-local"
                                value={form.scheduled_start}
                                onChange={(e) => set('scheduled_start', e.target.value)}
                                className={`${inputCls} min-h-12 py-3 text-lg`}
                            />
                        </div>

                        {/* Pickup */}
                        <div className="md:col-span-2">
                            <label className={labelCls}>Pickup Address</label>
                            <AddressAutocomplete label="Pickup Address" value={form.pickup_address} onChange={(value) => set('pickup_address', value)} placeholder="Where the trip starts" inputClassName={inputCls} />
                        </div>

                        {/* Dropoff */}
                        <div className="md:col-span-2">
                            <label className={labelCls}>Dropoff Address</label>
                            {selectedServiceLocations.length > 0 && (
                                <select
                                    aria-label="Fixed dropoff location"
                                    value={selectedServiceLocations.find((location) => location.address === form.dropoff_address)?.id || ''}
                                    onChange={(event) => {
                                        const location = selectedServiceLocations.find((candidate) => candidate.id === event.target.value);
                                        if (location) set('dropoff_address', location.address);
                                    }}
                                    className={`${inputCls} mb-2`}
                                >
                                    <option value="">Search or enter another address</option>
                                    {selectedServiceLocations.map((location) => <option key={location.id} value={location.id}>{location.label} — {location.address}</option>)}
                                </select>
                            )}
                            <AddressAutocomplete label="Dropoff Address" value={form.dropoff_address} onChange={(value) => set('dropoff_address', value)} placeholder="Where the trip ends" inputClassName={inputCls} />
                        </div>

                        {/* Zone is a separate selector over the tenant catalog. */}
                        <div>
                            <label className={labelCls}>Zone</label>
                            <select value={form.zone_id} onChange={(event) => chooseZone(event.target.value)} className={inputCls}>
                                <option value="">No zone selected</option>
                                {zones.map((zone) => <option key={zone.id} value={zone.id}>{zone.name}</option>)}
                            </select>
                        </div>

                        {/* Money tail — Module 6 owns the math; these are just captured values */}
                        {showTripPrices && <div>
                            <label className={labelCls}>{isQuote ? 'Quoted Amount' : 'Fare'}</label>
                            <div className="relative">
                                <span className={`absolute left-3 top-2 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>$</span>
                                <input type="number" step="0.01" min="0" value={calculatedFare} onChange={(e) => set('quoted_amount', e.target.value)} placeholder="0.00" className={`${inputCls} pl-7`} disabled={!canEditTripPrices} aria-readonly={!canEditTripPrices} />
                                {selectedService && <p className="mt-1 text-xs text-[var(--ink-muted)]">Starts with the service base fare and selected zone rate. Dispatchers may set or reset it.</p>}
                            </div>
                        </div>}
                        {showTripPrices && <div>
                            <label className={labelCls}>Final Amount</label>
                            <div className="relative">
                                <span className={`absolute left-3 top-2 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>$</span>
                                <input type="number" step="0.01" min="0" value={form.final_amount} onChange={(e) => set('final_amount', e.target.value)} placeholder="0.00" className={`${inputCls} pl-7`} disabled={!canEditFinalAmounts} />
                            </div>
                        </div>}

                        {/* Payment status */}
                        {showPaymentStatus && <div className="md:col-span-2">
                            <label className={labelCls}>Payment Status</label>
                            <input type="text" value={form.payment_status.replaceAll('_', ' ')} className={inputCls} readOnly aria-readonly="true" />
                        </div>}

                        {/* FA-2: Flight. Airport trips are tracked by FlightAware when the service has Track flights on. */}
                        <fieldset className="md:col-span-2 rounded-lg border border-[var(--surface-border)] p-3" aria-label="Flight">
                            <legend className={`px-1 text-sm font-medium ${dark ? 'text-gray-300' : 'text-gray-700'}`}>Flight (airport trips)</legend>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                                <div className="md:col-span-2">
                                    <label className={labelCls} htmlFor="engagement-flight-info">Airline + flight number</label>
                                    <input id="engagement-flight-info" type="text" value={form.flight_info} onChange={(e) => set('flight_info', e.target.value)}
                                        placeholder="DL30 or Delta 30" className={inputCls} autoComplete="off" />
                                </div>
                                <div>
                                    <label className={labelCls} htmlFor="engagement-flight-direction">Direction</label>
                                    <select id="engagement-flight-direction" value={form.flight_direction} onChange={(e) => set('flight_direction', e.target.value)} className={inputCls}>
                                        <option value="">{(() => {
                                            const guess = inferFlightDirection({ pickup: form.pickup_address, dropoff: form.dropoff_address }).direction;
                                            return guess ? `From the addresses (${guess})` : 'From the addresses';
                                        })()}</option>
                                        <option value="arriving">Arriving</option>
                                        <option value="departing">Departing</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls} htmlFor="engagement-flight-terminal">Terminal</label>
                                    <select id="engagement-flight-terminal" value={form.flight_terminal} onChange={(e) => set('flight_terminal', e.target.value)} className={inputCls}>
                                        <option value="">Not sure</option>
                                        <option value="domestic">Domestic</option>
                                        <option value="international">International</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls} htmlFor="engagement-flight-airport">Airport</label>
                                    <input id="engagement-flight-airport" type="text" maxLength={4} value={form.flight_airport} onChange={(e) => set('flight_airport', e.target.value.toUpperCase())}
                                        placeholder={inferFlightDirection({ pickup: form.pickup_address, dropoff: form.dropoff_address }).airportCode || 'ATL'} className={inputCls} />
                                </div>
                            </div>
                            <p className={`text-xs mt-2 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Arrival pickups follow your company&apos;s airport rule. A flight change never moves the pickup by itself: dispatch sees a suggested pickup to approve.</p>
                        </fieldset>

                        {/* Notes */}
                        <div className="md:col-span-2">
                            <label className={labelCls}>Notes / Special Instructions</label>
                            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows="3" placeholder="Anything the driver or dispatcher should know…" className={inputCls} />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={`sticky bottom-0 border-t px-6 py-4 flex justify-end gap-3 ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className={`px-4 py-2 rounded-lg font-medium border ${dark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} disabled:opacity-50`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50"
                    >
                        {saving ? 'Saving…' : isEdit ? 'Save Changes' : `Create ${words.booking}`}
                    </button>
                </div>
            </ModalSurface>
    );
}

export default EngagementFormModal;
